import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileDown, 
  Loader2, 
  Users, 
  Calendar, 
  Star, 
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  ChevronDown
} from 'lucide-react';
import { useUserProfile } from '../hooks/useUserProfile';
import { useApiClient } from '../hooks/useApiClient';
import { FeedbackRequest } from '../types/feedback';
import { formatDateInIST, mapApiFeedbackRequestFull } from '../utils/mappers';
import { ReporteeInfo } from '../types/userProfile';
import { YEARS, QUARTERS } from '../config/feedbackConfig';
import { printHtmlInNewWindow } from '../utils/pdfTemplates';

export function ExportFeedbackPage() {
  const navigate = useNavigate();
  const { userProfile, loading: profileLoading, fetchUserProfile } = useUserProfile();
  const { apiCall } = useApiClient();
  
  // Selection state
  const [selectedEmployee, setSelectedEmployee] = useState<ReporteeInfo | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  
  // Data state
  const [feedbackRequests, setFeedbackRequests] = useState<FeedbackRequest[]>([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dropdown states
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  
  const printRef = useRef<HTMLDivElement>(null);

  // Fetch user profile on mount
  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if no direct reports
  useEffect(() => {
    if (!profileLoading && userProfile && (!userProfile.directReports || userProfile.directReports.length === 0)) {
      navigate('/dashboard');
    }
  }, [profileLoading, userProfile, navigate]);

  // Fetch feedback when employee and year are selected
  useEffect(() => {
    if (selectedEmployee && selectedYear) {
      fetchEmployeeFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, selectedYear]);

  const fetchEmployeeFeedback = async () => {
    if (!selectedEmployee) return;
    
    setLoadingFeedback(true);
    setError(null);
    
    try {
      // Fetch all feedback requests for the employee for the selected year
      // Using the my-feedback endpoint which returns feedback by employee ID
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/my-feedback/${encodeURIComponent(selectedEmployee.employeeId)}`
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          // Filter by year and fetch full details for each completed request
          const yearRequests = result.data.filter(
            (req: any) => req.year === selectedYear && req.status === 'completed'
          );
          
          // Fetch full details for each request
          const detailedRequests = await Promise.all(
            yearRequests.map(async (req: any) => {
              try {
                const detailResponse = await apiCall(
                  `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${req.id}`
                );
                if (detailResponse.ok) {
                  const detailResult = await detailResponse.json();
                  return mapApiFeedbackRequestFull(detailResult.data);
                }
              } catch (err) {
                console.error('Error fetching request details:', err);
              }
              return null;
            })
          );
          
          setFeedbackRequests(detailedRequests.filter(Boolean) as FeedbackRequest[]);
        }
      } else {
        setError('Failed to fetch feedback requests');
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('An error occurred while fetching feedback');
    } finally {
      setLoadingFeedback(false);
    }
  };

  // Calculate statistics
  const statistics = useMemo(() => {
    if (feedbackRequests.length === 0) {
      return {
        overallAverage: null,
        quarterlyRatings: {} as Record<string, { managerRating: number | null; selfRating: number | null }>,
        totalQuarters: 0
      };
    }

    const ratingsWithValues = feedbackRequests.filter(r => r.managerRating && r.managerRating > 0);
    const overallAverage = ratingsWithValues.length > 0
      ? ratingsWithValues.reduce((sum, r) => sum + (r.managerRating || 0), 0) / ratingsWithValues.length
      : null;

    const quarterlyRatings: Record<string, { managerRating: number | null; selfRating: number | null; request: FeedbackRequest }> = {};
    
    QUARTERS.forEach(quarter => {
      const quarterRequest = feedbackRequests.find(r => r.quarter === quarter);
      if (quarterRequest) {
        quarterlyRatings[quarter] = {
          managerRating: quarterRequest.managerRating || null,
          selfRating: quarterRequest.selfRating || null,
          request: quarterRequest
        };
      }
    });

    return {
      overallAverage,
      quarterlyRatings,
      totalQuarters: Object.keys(quarterlyRatings).length
    };
  }, [feedbackRequests]);

  // Generate and export PDF
  const handleExportPDF = async () => {
    if (!selectedEmployee || feedbackRequests.length === 0) return;
    
    setExporting(true);
    
    try {
      // Create the PDF content as HTML
      const pdfContent = generatePDFContent();
      printHtmlInNewWindow(pdfContent);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const generatePDFContent = (): string => {
    const employeeName = selectedEmployee?.displayName || 'Employee';
    const managerName = userProfile?.displayName || 'Manager';
    const currentDate = formatDateInIST(new Date());

    const escapeHtml = (value: string): string =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const formatText = (value?: string): string => {
      if (!value || !value.trim()) return 'Not provided';
      return escapeHtml(value).replace(/\r?\n/g, '<br />');
    };

    const formatRating = (rating: number | null | undefined): string =>
      typeof rating === 'number' && Number.isFinite(rating) ? `${rating.toFixed(1)} / 5.0` : 'Not rated';

    const quarterlyContent = QUARTERS.map(quarter => {
      const data = statistics.quarterlyRatings[quarter];
      if (!data) return '';
      
      const request = (data as any).request as FeedbackRequest;
      
      return `
        <section class="quarter-card">
          <div class="quarter-head">
            <h3>${quarter} ${selectedYear}</h3>
          </div>
          <div class="quarter-body">
            <div class="rating-row">
              <div class="rating-item">
                <span>Manager Rating</span>
                <strong>${formatRating(data.managerRating)}</strong>
              </div>
              <div class="rating-item">
                <span>Self Rating</span>
                <strong>${formatRating(data.selfRating)}</strong>
              </div>
            </div>
            
            ${request.answers && request.answers.length > 0 ? `
              <div class="feedback-group">
                <h4>Feedback Details</h4>
                ${request.answers.map((answer, idx) => `
                  <article class="qa-item">
                    <div class="qa-question">${idx + 1}. ${formatText(answer.question || `Question ${idx + 1}`)}</div>
                    <div class="qa-response">
                      <span>Employee Response</span>
                      <p>${formatText(answer.answer)}</p>
                    </div>
                    ${answer.managerFeedback && answer.managerFeedback.trim() ? `
                      <div class="qa-response manager">
                        <span>Manager Feedback</span>
                        <p>${formatText(answer.managerFeedback)}</p>
                      </div>
                    ` : `
                      <div class="qa-response manager">
                        <span>Manager Feedback</span>
                        <p class="muted">No manager feedback provided.</p>
                      </div>
                    `}
                  </article>
                `).join('')}
              </div>
            ` : '<p class="empty-message">No completed responses for this quarter.</p>'}

            <div class="qa-response manager overall-comments">
              <span>Manager Overall Comments</span>
              <p>${formatText(request.overallComments)}</p>
            </div>
          </div>
        </section>
      `;
    }).filter(Boolean).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Connect Report - ${escapeHtml(employeeName)} - ${selectedYear}</title>
        <style>
          @page { size: A4; margin: 14mm; }
          @media print {
            body {
              margin: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: "Segoe UI", Tahoma, Arial, sans-serif;
            line-height: 1.5;
            color: #0f172a;
            background: #ffffff;
            font-size: 12px;
          }
          .sheet {
            max-width: 210mm;
            margin: 0 auto;
          }
          .hero {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #db2777 100%);
            color: #ffffff;
            border-radius: 14px;
            padding: 22px 24px;
            margin-bottom: 14px;
          }
          .hero h1 {
            margin: 0 0 12px 0;
            font-size: 24px;
            line-height: 1.2;
          }
          .hero-meta {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px 16px;
            font-size: 11px;
          }
          .hero-meta strong {
            display: block;
            font-size: 12px;
            margin-top: 2px;
          }
          .employee-card {
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 12px 14px;
            margin-bottom: 14px;
            background: #f8fafc;
          }
          .employee-name {
            margin: 0 0 5px 0;
            font-size: 18px;
            font-weight: 700;
          }
          .employee-meta {
            margin: 2px 0;
            color: #475569;
            font-size: 11px;
          }
          .stats {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 10px;
            margin-bottom: 14px;
          }
          .stat {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            background: #ffffff;
            padding: 10px 12px;
          }
          .stat-label {
            color: #64748b;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .stat-value {
            margin-top: 6px;
            font-size: 16px;
            font-weight: 700;
            color: #0f172a;
          }
          .quarter-card {
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            overflow: hidden;
            margin-bottom: 12px;
            page-break-inside: avoid;
          }
          .quarter-head {
            background: #f1f5f9;
            border-bottom: 1px solid #e2e8f0;
            padding: 10px 14px;
          }
          .quarter-head h3 {
            margin: 0;
            font-size: 14px;
          }
          .quarter-body {
            padding: 12px 14px;
          }
          .rating-row {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
            margin-bottom: 10px;
          }
          .rating-item {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            background: #f8fafc;
            padding: 8px 10px;
          }
          .rating-item span {
            display: block;
            color: #64748b;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .rating-item strong {
            display: block;
            margin-top: 4px;
            font-size: 13px;
          }
          .feedback-group h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #334155;
          }
          .qa-item {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 10px;
            margin-bottom: 8px;
            background: #ffffff;
          }
          .qa-question {
            margin-bottom: 8px;
            font-size: 12px;
            font-weight: 700;
            color: #0f172a;
          }
          .qa-response {
            margin-top: 6px;
            padding: 8px 9px;
            border-radius: 8px;
            background: #f0fdfa;
            border: 1px solid #99f6e4;
          }
          .qa-response.manager {
            background: #eff6ff;
            border-color: #bfdbfe;
          }
          .overall-comments {
            margin-top: 10px;
          }
          .qa-response span {
            display: block;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #334155;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .qa-response p {
            margin: 0;
            color: #1e293b;
            line-height: 1.65;
          }
          .muted {
            color: #64748b;
            font-style: italic;
          }
          .empty-message {
            text-align: center;
            border: 1px dashed #cbd5e1;
            border-radius: 8px;
            color: #64748b;
            margin: 0;
            padding: 14px;
          }
          .footer {
            margin-top: 18px;
            border-top: 1px solid #e2e8f0;
            padding-top: 8px;
            text-align: center;
            color: #64748b;
            font-size: 10px;
          }
        </style>
      </head>
      <body>
        <main class="sheet">
          <header class="hero">
            <h1>Connect Annual Report</h1>
            <div class="hero-meta">
              <div>Employee<strong>${escapeHtml(employeeName)}</strong></div>
              <div>Manager<strong>${escapeHtml(managerName)}</strong></div>
              <div>Review Year<strong>${selectedYear}</strong></div>
              <div>Generated On<strong>${currentDate}</strong></div>
            </div>
          </header>

          <section class="employee-card">
            <h2 class="employee-name">${escapeHtml(employeeName)}</h2>
            <p class="employee-meta">${escapeHtml(selectedEmployee?.jobTitle || 'Employee')} | ${escapeHtml(selectedEmployee?.department || 'Department')}</p>
            <p class="employee-meta">Employee ID: ${escapeHtml(selectedEmployee?.employeeId || 'N/A')}</p>
            <p class="employee-meta">Email: ${escapeHtml(selectedEmployee?.email || 'N/A')}</p>
          </section>

          <section class="stats">
            <article class="stat">
              <div class="stat-label">Overall Average</div>
              <div class="stat-value">${statistics.overallAverage ? statistics.overallAverage.toFixed(1) : 'N/A'}</div>
            </article>
            <article class="stat">
              <div class="stat-label">Quarters Reviewed</div>
              <div class="stat-value">${statistics.totalQuarters} / 4</div>
            </article>
            <article class="stat">
              <div class="stat-label">Report Year</div>
              <div class="stat-value">${selectedYear}</div>
            </article>
          </section>

          ${quarterlyContent || '<p class="empty-message">No completed connect responses found for this year.</p>'}

          <footer class="footer">
            <p>This report was automatically generated by the Feedback Management System.</p>
            <p>Confidential - Internal Use Only</p>
          </footer>
        </main>
      </body>
      </html>
    `;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setEmployeeDropdownOpen(false);
      setYearDropdownOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (profileLoading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-pink-500 animate-spin" />
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const directReports = userProfile?.directReports || [];

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-pink-400 hover:text-pink-300 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Dashboard</span>
        </button>

        {/* Page Header */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center gap-4 mb-2">
            <FileDown className="w-10 h-10" />
            <h1 className="text-3xl font-bold">Export Connect Report</h1>
          </div>
          <p className="text-white/80 ml-14">
            Generate a comprehensive PDF report of employee connect for a selected year
          </p>
        </div>

        {/* Selection Form */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-500" />
            Select Employee & Year
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employee Selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Employee
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEmployeeDropdownOpen(!employeeDropdownOpen);
                    setYearDropdownOpen(false);
                  }}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-left flex items-center justify-between hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <span className={selectedEmployee ? 'text-slate-900' : 'text-slate-400'}>
                    {selectedEmployee ? selectedEmployee.displayName : 'Select an employee...'}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${employeeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {employeeDropdownOpen && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                    {directReports.map((employee) => (
                      <button
                        key={employee.employeeId}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmployee(employee);
                          setEmployeeDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-purple-50 flex items-center gap-3 transition-colors ${
                          selectedEmployee?.employeeId === employee.employeeId ? 'bg-purple-50 text-purple-700' : 'text-slate-700'
                        }`}
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                          {employee.displayName.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium">{employee.displayName}</div>
                          <div className="text-xs text-slate-500">{employee.jobTitle}</div>
                        </div>
                        {selectedEmployee?.employeeId === employee.employeeId && (
                          <CheckCircle className="w-5 h-5 text-purple-500 ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Year Selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Year
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setYearDropdownOpen(!yearDropdownOpen);
                    setEmployeeDropdownOpen(false);
                  }}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-left flex items-center justify-between hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <span className="text-slate-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {selectedYear}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${yearDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {yearDropdownOpen && (
                  <div className="absolute z-10 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                    {YEARS.map((year) => (
                      <button
                        key={year}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedYear(year);
                          setYearDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-purple-50 flex items-center justify-between transition-colors ${
                          selectedYear === year ? 'bg-purple-50 text-purple-700' : 'text-slate-700'
                        }`}
                      >
                        <span>{year}</span>
                        {selectedYear === year && (
                          <CheckCircle className="w-5 h-5 text-purple-500" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        {selectedEmployee && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Feedback Summary for {selectedYear}
              </h2>
              
              <button
                onClick={handleExportPDF}
                disabled={exporting || loadingFeedback || feedbackRequests.length === 0}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <FileDown className="w-5 h-5" />
                    <span>Export PDF</span>
                  </>
                )}
              </button>
            </div>

            {loadingFeedback ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                <span className="ml-3 text-slate-600">Loading feedback data...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12 text-red-500">
                <AlertCircle className="w-6 h-6 mr-2" />
                <span>{error}</span>
              </div>
            ) : feedbackRequests.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileDown className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Completed Feedback</h3>
                <p className="text-slate-500">
                  There are no completed feedback records for {selectedEmployee.displayName} in {selectedYear}.
                </p>
              </div>
            ) : (
              <div ref={printRef}>
                {/* Overall Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-5 text-center">
                    <p className="text-sm text-amber-700 mb-1">Overall Average Rating</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {statistics.overallAverage ? statistics.overallAverage.toFixed(1) : 'N/A'}
                    </p>
                    {statistics.overallAverage && (
                      <div className="flex justify-center gap-1 mt-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${
                              star <= Math.round(statistics.overallAverage!)
                                ? 'text-yellow-500 fill-yellow-500'
                                : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 text-center">
                    <p className="text-sm text-blue-700 mb-1">Quarters Reviewed</p>
                    <p className="text-3xl font-bold text-blue-600">{statistics.totalQuarters}</p>
                    <p className="text-sm text-blue-500 mt-1">of 4 quarters</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 text-center">
                    <p className="text-sm text-green-700 mb-1">Employee</p>
                    <p className="text-lg font-bold text-green-600 truncate">{selectedEmployee.displayName}</p>
                    <p className="text-sm text-green-500 mt-1">{selectedEmployee.jobTitle}</p>
                  </div>
                </div>

                {/* Quarterly Breakdown */}
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Quarterly Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {QUARTERS.map((quarter) => {
                    const data = statistics.quarterlyRatings[quarter];
                    return (
                      <div
                        key={quarter}
                        className={`border rounded-xl p-5 ${
                          data
                            ? 'border-purple-200 bg-purple-50/50'
                            : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold text-slate-900">{quarter} {selectedYear}</span>
                          {data ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              Completed
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs font-medium rounded-full">
                              No Data
                            </span>
                          )}
                        </div>
                        
                        {data ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">Manager Rating:</span>
                              <div className="flex items-center gap-1">
                                {data.managerRating ? (
                                  <>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`w-4 h-4 ${
                                          star <= data.managerRating!
                                            ? 'text-yellow-500 fill-yellow-500'
                                            : 'text-slate-300'
                                        }`}
                                      />
                                    ))}
                                    <span className="ml-1 font-semibold text-slate-700">
                                      ({data.managerRating})
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-slate-400">N/A</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">Self Rating:</span>
                              <div className="flex items-center gap-1">
                                {data.selfRating ? (
                                  <>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`w-4 h-4 ${
                                          star <= data.selfRating!
                                            ? 'text-blue-500 fill-blue-500'
                                            : 'text-slate-300'
                                        }`}
                                      />
                                    ))}
                                    <span className="ml-1 font-semibold text-slate-700">
                                      ({data.selfRating})
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-slate-400">N/A</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">No feedback submitted for this quarter</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
